import { useEffect, useState, useCallback } from 'react';
import { authFetch, authFetchRequest } from '../../utils/api';
import { useCompany } from '../../contexts/CompanyContext';
import { formatDateTime } from '../../utils/time';
import './notifications.css';

const NotificationsInbox = ({ onUnreadCountChange }) => {
  const { companyData } = useCompany();
  const tz = companyData?.timezone;
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await authFetch('/notifications/inbox?limit=20');
      setItems(Array.isArray(data) ? data : []);
      if (onUnreadCountChange) {
        const c = (data || []).filter((n) => !n.is_read).length;
        onUnreadCountChange(c);
      }
    } catch (err) {
      console.error('Failed to load notifications', err);
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (item) => {
    const opening = expandedId !== item.id;
    setExpandedId(opening ? item.id : null);
    // Auto-mark as read when first opened
    if (opening && !item.is_read) {
      try {
        await authFetchRequest(`/notifications/${item.id}`, { is_read: true }, 'PATCH');
        setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
        window.dispatchEvent(new Event('notifications-updated')); // [VP-NOTIF-BADGE 2026-05-28] refresh global badge
        if (onUnreadCountChange) {
          const newCount = items.filter((n) => !n.is_read && n.id !== item.id).length;
          onUnreadCountChange(newCount);
        }
      } catch (err) {
        console.error('Failed to mark as read', err);
      }
    }
  };

  const toggleRead = async (e, item) => {
    e.stopPropagation();
    const next = !item.is_read;
    try {
      await authFetchRequest(`/notifications/${item.id}`, { is_read: next }, 'PATCH');
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: next } : n)));
      window.dispatchEvent(new Event('notifications-updated')); // [VP-NOTIF-BADGE 2026-05-28] refresh global badge
      if (onUnreadCountChange) {
        const newItems = items.map((n) => (n.id === item.id ? { ...n, is_read: next } : n));
        onUnreadCountChange(newItems.filter((n) => !n.is_read).length);
      }
    } catch (err) {
      console.error('Failed to toggle read', err);
    }
  };

  if (loading) return <div className="notif-section">Loading notifications…</div>;

  return (
    <div className="notif-section">
      <div className="notif-header">
        <button className="notif-refresh" onClick={load} title="Refresh">↻ Refresh</button>
      </div>

      {items.length === 0 ? (
        <p className="notif-empty">No notifications yet.</p>
      ) : (
        <ul className="notif-list">
          {items.map((n) => (
            <li
              key={n.id}
              className={`notif-item ${n.is_read ? 'read' : 'unread'}`}
              onClick={() => toggleExpand(n)}
            >
              <div className="notif-row">
                <span className="notif-subject">{n.subject || '(no subject)'}</span>
                <span className="notif-meta">
                  {n.sender_name} · {formatDateTime(n.created_at, tz)}
                </span>
                <button
                  className="notif-toggle"
                  onClick={(e) => toggleRead(e, n)}
                  title={n.is_read ? 'Mark as unread' : 'Mark as read'}
                >
                  {n.is_read ? 'unread' : 'read'}
                </button>
              </div>
              {expandedId === n.id && (
                <div
                  className="notif-body"
                  dangerouslySetInnerHTML={{ __html: n.body || '' }}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NotificationsInbox;
